import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  applyDocumentLanguage,
  DEFAULT_LANGUAGE,
  getInitialAppLanguage,
  isAppLanguage,
  storeLanguage,
  type AppLanguage,
} from './language';
import { resources } from './resources';

const initialLanguage = getInitialAppLanguage();

// Direction is correct before React paints, including when a stored choice exists.
applyDocumentLanguage(initialLanguage);

export const i18n = createInstance();

i18n.on('languageChanged', (language) => {
  if (isAppLanguage(language)) {
    applyDocumentLanguage(language);
  }
});

export const i18nReady = i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  supportedLngs: ['en', 'ar'],
  fallbackLng: false,
  defaultNS: 'translation',
  ns: ['translation'],
  load: 'languageOnly',
  nonExplicitSupportedLngs: false,
  returnNull: false,
  returnEmptyString: false,
  initAsync: false,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

export interface ChangeAppLanguageOptions {
  readonly persist?: boolean;
}

export async function changeAppLanguage(
  language: AppLanguage,
  options: ChangeAppLanguageOptions = {},
): Promise<void> {
  const { persist = true } = options;

  // Apply direction synchronously; translation resources are bundled and switch locally.
  applyDocumentLanguage(language);

  if (persist) {
    storeLanguage(language);
  }

  if (!i18n.isInitialized) {
    await i18nReady;
  }

  if (i18n.resolvedLanguage !== language) {
    await i18n.changeLanguage(language);
  }
}

export function getCurrentLanguage(): AppLanguage {
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;
  return isAppLanguage(currentLanguage) ? currentLanguage : DEFAULT_LANGUAGE;
}
